import { useMemo } from 'react';

import { VISITOR_NATIVE_APP_ID, NativeAppStatus } from 'utils/nativeApps';

import { useFeatureFlag } from './useFeatureFlag';
import { useOpQuery } from './useOpQuery';
import { useFeatureGate } from './useFeatureGate';

export const useIsVisitorEnabled = ({ orgId }: { orgId: number }) => {
  // Visitor is a tiered feature (no single 'visitor' code exists in Helium),
  // so the org has Visitor if it has ANY of the tier features.
  const { hasFeature: hasVisitorSmall } = useFeatureGate('visitorSmall', orgId);
  const { hasFeature: hasVisitorMedium } = useFeatureGate(
    'visitorMedium',
    orgId,
  );
  const { hasFeature: hasVisitorLarge } = useFeatureGate('visitorLarge', orgId);

  const hasVisitorFeature =
    hasVisitorSmall || hasVisitorMedium || hasVisitorLarge;

  const { data: orgNativeApps } = useOpQuery({
    apiEndpointName: 'listOrgNativeApps',
    parameters: [orgId],
    select: (heliumResponse) => heliumResponse.json.data ?? [],
  });

  const hasVisitorNativeApp = orgNativeApps?.some(
    (app) =>
      app.nativeAppId === VISITOR_NATIVE_APP_ID &&
      app.status === NativeAppStatus.Active,
  );

  // TODO - Hide visitor in table for now. Update when no longer determined by a feature flag.
  const { value: isVisitorEnabledFF } = useFeatureFlag<boolean>(
    'ENABLE_VISITOR',
    orgId,
  );

  const isVisitorEnabled = useMemo(() => {
    return hasVisitorNativeApp || isVisitorEnabledFF || hasVisitorFeature;
  }, [hasVisitorFeature, hasVisitorNativeApp, isVisitorEnabledFF]);

  return isVisitorEnabled;
};
